import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Heading,
  useToast,
  Button,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  useColorModeValue,
  Center,
  VStack,
  Text,
  UnorderedList,
  ListItem,
  SimpleGrid,
  Container,
  HStack,
  Select,
} from "@chakra-ui/react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";

import Navbar from "../../components/Navbar";
import useAxios from "../../utils/useAxios";
import IrrigationControllerTile from "../../components/IrrigationComponents/IrrigationControllerTile";
import { IrrigationOverviewType } from "../../types/irrigationInterface";
import { IDevice } from "../../types/deviceInterface";
import {
  getUserTimezone,
  convertLocalTimeToUTC,
  convertUTCTimeToLocal,
} from "../../utils/dateUtils";

const ConfigureIrrigation: React.FC<{ irrigation: IrrigationOverviewType }> = ({ irrigation }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showDescription, setShowDescription] = useState<boolean>(false);

  const [sensor, setSensor] = useState<number | null>(irrigation.user_device_sensor);
  const [mode, setMode] = useState<number>(irrigation.mode);
  // Convert UTC times from server to local time for display
  const [startTime, setStartTime] = useState<string | null>(
    irrigation.start_time ? convertUTCTimeToLocal(irrigation.start_time) : null,
  );
  const [endTime, setEndTime] = useState<string | null>(
    irrigation.end_time ? convertUTCTimeToLocal(irrigation.end_time) : null,
  );
  const [period, setPeriod] = useState<number | null>(irrigation.period);
  const [setPoint, setSetPoint] = useState<number | null>(irrigation.setpoint);
  const secondsTotal = irrigation.duration ? irrigation.duration : 0;
  const [dutyCycleHours, setDutyCycleHours] = useState<number>(Math.floor(secondsTotal / 3600));
  const [dutyCycleMinutes, setDutyCycleMinutes] = useState<number>(
    Math.floor((secondsTotal % 3600) / 60),
  );
  const [dutyCycleSeconds, setDutyCycleSeconds] = useState<number>(Math.round(secondsTotal % 60));

  const [hours, setHours] = useState<number>(
    irrigation.period ? Math.floor(irrigation.period / 3600) : 0,
  );
  const [minutes, setMinutes] = useState<number>(
    irrigation.period ? Math.floor((irrigation.period % 3600) / 60) : 0,
  );
  const [seconds, setSeconds] = useState<number>(irrigation.period ? irrigation.period % 60 : 0);

  const [devices, setDevices] = useState<IDevice[] | undefined>(undefined);
  const [meterStartDateTime, setMeterStartDateTime] = useState<string | null>(
    irrigation.water_meter_start_date,
  );

  const boxColor = useColorModeValue("white", "gray.900");
  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const api = useAxios();
  const userTimezone = getUserTimezone();

  const handleConfigure = async () => {
    try {
      setLoading(true);
      // calculate total duration in seconds
      const totalDuration = dutyCycleHours * 3600 + dutyCycleMinutes * 60 + dutyCycleSeconds;

      // Convert local times to UTC before sending to server
      const utcStartTime = startTime ? convertLocalTimeToUTC(startTime) : null;
      const utcEndTime = endTime ? convertLocalTimeToUTC(endTime) : null;

      const response = await api.put("/agtech/irrigation/" + id + "/", {
        user_device_sensor: sensor,
        mode,
        start_time: utcStartTime,
        end_time: utcEndTime,
        period,
        duration: totalDuration,
        setpoint: setPoint,
        water_meter_start_date: meterStartDateTime,
      });
      toast({
        title: "Controller configured",
        description: "The controller has been configured successfully",
        status: "success",
        duration: 9000,
        isClosable: true,
      });
      navigate(`/irrigation/`);
    } catch (error: any) {
      setError(error.message);
      toast({
        title: "Error configuring controller",
        description: error.message,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = () => {
    const totalSeconds = hours * 3600 + minutes * 60 + seconds;
    setPeriod(totalSeconds);
  };

  useEffect(() => {
    handlePeriodChange();
  }, [hours, minutes, seconds]);

  useEffect(() => {
    // Get devices
    api
      .get("/frontend/user-devices/?filter_device_type=1")
      .then((response) => {
        console.log("Devices fetched", response.data);
        setDevices(response.data);
      })
      .catch((error) => {
        console.error("Error fetching devices", error);
        toast({
          title: "Error fetching devices",
          description: error.message,
          status: "error",
          isClosable: true,
        });
      });
  }, []);

  return (
    <Container p={5} bg={boxColor} borderRadius={8}>
      <VStack>
        <Heading size="lg">
          <Trans>Configure Controller</Trans>
        </Heading>
        <Button variant="outline" size="sm" onClick={() => setShowDescription(!showDescription)}>
          {showDescription ? <Trans>Hide Description</Trans> : <Trans>View Description</Trans>}
        </Button>
        {showDescription && (
          <Box>
            <Text mt={2}>
              <Trans>The irrigation controller supports three modes:</Trans>
            </Text>
            <UnorderedList>
              <ListItem>
                <Trans>Manual: Manual opening and closing</Trans>
              </ListItem>
              <ListItem>
                <Trans>Schedule: The controller will be on between a start and end time</Trans>
              </ListItem>
              <ListItem>
                <Trans>
                  Auto: The controller will automatically adjust the duration to reach the desired
                  moisture
                </Trans>
              </ListItem>
            </UnorderedList>
            <Text>
              <Trans>
                Interval - how often the valve is opened within the given time. More often will
                consume more power
              </Trans>
            </Text>
            <Text>
              <Trans>Duration - how long the valve should stay open. </Trans>
            </Text>
            <Text>
              <Trans>
                Currently start time and end time only supports whole hours, and minutes will be
                ignored
              </Trans>
            </Text>
          </Box>
        )}
        <FormControl id="device">
          <FormLabel>
            <Trans>Attached sensor</Trans>
          </FormLabel>
          <Select
            value={String(sensor)}
            onChange={(e) => setSensor(parseInt(e.target.value))}
            placeholder={t`Select device`}
          >
            {devices?.map((device) => (
              <option key={device.device_id} value={device.device_id}>
                {device.device_name ? device.device_name : <>Device # {device.device_id}</>}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl id="mode">
          <FormLabel>
            <Trans>Mode</Trans>
          </FormLabel>
          <Select value={mode ?? ""} onChange={(e) => setMode(parseInt(e.target.value))}>
            <option value={0}>
              <Trans>Manual</Trans>
            </option>
            <option value={1}>
              <Trans>Schedule</Trans>
            </option>
          </Select>
        </FormControl>
        {(mode === 1 || mode === 2) && (
          <>
            <FormControl id="start_time">
              <FormLabel>
                <Trans>Start Time</Trans> ({userTimezone})
              </FormLabel>
              <Input
                type="time"
                value={startTime ?? ""}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </FormControl>
            <FormControl id="end_time">
              <FormLabel>
                <Trans>End Time</Trans> ({userTimezone})
              </FormLabel>
              <Input
                type="time"
                value={endTime ?? ""}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </FormControl>
            <FormControl id="interval" isRequired>
              <FormLabel>
                <Trans>Interval</Trans>
              </FormLabel>
              <HStack>
                <Input
                  maxWidth={"15%"}
                  type="number"
                  placeholder={t`Hours`}
                  value={hours}
                  onChange={(e) => setHours(parseInt(e.target.value))}
                />
                <Text m={2}>
                  <Trans>hr</Trans>
                </Text>
                <Input
                  maxWidth={"15%"}
                  type="number"
                  placeholder={t`Minutes`}
                  value={minutes}
                  onChange={(e) => setMinutes(parseInt(e.target.value))}
                />
                <Text m={2}>
                  <Trans>min</Trans>
                </Text>
                <Input
                  maxWidth={"15%"}
                  type="number"
                  placeholder={t`Seconds`}
                  value={seconds}
                  onChange={(e) => setSeconds(parseInt(e.target.value))}
                />
                <Text m={2}>
                  <Trans>sec</Trans>
                </Text>
              </HStack>
            </FormControl>
            <FormControl id="duration" isRequired>
              <FormLabel>
                <Trans>Duration</Trans>
              </FormLabel>
              <HStack mt={2}>
                <Input
                  maxWidth={"15%"}
                  type="number"
                  placeholder={t`Hours`}
                  value={dutyCycleHours}
                  onChange={(e) => setDutyCycleHours(parseInt(e.target.value))}
                />
                <Text m={2}>
                  <Trans>hr</Trans>
                </Text>
                <Input
                  maxWidth={"15%"}
                  type="number"
                  placeholder={t`Minutes`}
                  value={dutyCycleMinutes}
                  onChange={(e) => setDutyCycleMinutes(parseInt(e.target.value))}
                />
                <Text m={2}>
                  <Trans>min</Trans>
                </Text>
                <Input
                  maxWidth={"15%"}
                  type="number"
                  placeholder={t`Seconds`}
                  value={dutyCycleSeconds}
                  onChange={(e) => setDutyCycleSeconds(parseInt(e.target.value))}
                />
                <Text m={2}>
                  <Trans>sec</Trans>
                </Text>
              </HStack>
            </FormControl>
          </>
        )}
        {mode === 2 && (
          <FormControl id="setpoint" isRequired>
            <FormLabel>
              <Trans>Setpoint</Trans>
            </FormLabel>
            <Input
              type="number"
              value={setPoint ?? ""}
              onChange={(e) => setSetPoint(parseInt(e.target.value))}
            />
          </FormControl>
        )}

        {irrigation.flow_measurement && (
          <FormControl id="meter_start_time">
            <FormLabel>
              <Trans>Water meter start time</Trans>
            </FormLabel>
            <Input
              type="date"
              value={meterStartDateTime?.split(" ")[0] ?? ""}
              onChange={(e) => setMeterStartDateTime(e.target.value)}
            />
          </FormControl>
        )}

        <Button
          onClick={() => {
            handleConfigure();
          }}
        >
          <Trans>Save</Trans>
        </Button>
      </VStack>
    </Container>
  );
};
const IrrigationController = () => {
  const [controller, setController] = useState<IrrigationOverviewType>();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const toast = useToast();
  const navigate = useNavigate();

  const { id } = useParams();

  const api = useAxios();

  useEffect(() => {
    const fetchController = async () => {
      try {
        const response = await api.get("/agtech/irrigation/" + id);
        setController(response.data);

        // Fetch pending command status
        try {
          const pendingResponse = await api.get(`/agtech/irrigation/${id}/pending-command/`);
          setController((prev) =>
            prev ? { ...prev, pending_valve_command: pendingResponse.data } : response.data,
          );
        } catch (err) {
          console.log("Error fetching pending command status");
        }

        setLoading(false);
      } catch (error: any) {
        setError(error.message);
        setLoading(false);
        toast({
          title: "Error fetching controller",
          description: error.message,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
    };

    fetchController();
  }, []);

  if (!controller) {
    return (
      <Navbar>
        <Container maxW="container.2xl" py={10}>
          <Heading mb={4}>
            <Trans>Irrigation Controller</Trans>
          </Heading>
          <Text>
            <Trans>Loading...</Trans>
          </Text>
        </Container>
      </Navbar>
    );
  }

  return (
    <Navbar>
      <Container maxW="container.2xl" py={10}>
        <Center>
          <VStack spacing={4}>
            <Heading mb={4}>
              <Trans>Irrigation Controller</Trans>
            </Heading>
            {loading && (
              <Text>
                <Trans>Loading...</Trans>
              </Text>
            )}
            {error && <Text>{error}</Text>}
            <SimpleGrid columns={{ base: 1 }} spacing={4}>
              <Center>
                <IrrigationControllerTile key={controller.id} irrigation={controller} />
              </Center>
              <ConfigureIrrigation irrigation={controller} />
            </SimpleGrid>
          </VStack>
        </Center>
      </Container>
    </Navbar>
  );
};

export default IrrigationController;
